import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { AuthService, ENVIRONMENT, TokenService } from '@cfp/core';
import { LoginCard, LoginLayout } from '@cfp/shared/ui/login';
import { LoadingSpinner } from '@cfp/ui/common';
import { TranslateModule } from '@ngx-translate/core';
import { LoginTokenResponseDTO, OauthSsoLoginService } from 'csr-login-app';
import { FooterText } from '../../components';
import { SsoOauthStateService } from './sso-oauth-state-service';

@Component({
  selector: 'cfp-sso-oauth-authentication-page',
  templateUrl: './sso-oauth-authentication-page.html',
  imports: [LoginLayout, LoginCard, LoadingSpinner, TranslateModule, FooterText],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SsoOauthAuthenticationPage implements OnInit {
  protected readonly isLoading = signal(true);
  protected readonly errorMessage = signal<string | undefined>(undefined);

  private readonly environment = inject(ENVIRONMENT);
  private readonly oauthSsoLoginService = inject(OauthSsoLoginService);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly tokenService = inject(TokenService);
  private readonly route = inject(ActivatedRoute);
  private readonly ssoOauthStateService = inject(SsoOauthStateService);

  public ngOnInit(): void {
    this.route.queryParams.subscribe((params: Params) => {
      if (!params.code || !params.state) {
        this.showErrorIfTokenIsInvalid('Missing code and/or state parameter');
        return;
      }
      if (!this.ssoOauthStateService.hasState(params.state)) {
        this.showErrorIfTokenIsInvalid('State parameter did not match the expected value');
        return;
      }

      this.oauthSsoLoginService
        .oauthSsoLoginPost({
          code: params.code,
          state: params.state,
          environmentCode: this.environment.environmentCode.dto
        })
        .subscribe({
          next: (response: LoginTokenResponseDTO) => {
            // todo: needs tobe refactored to accessToken only there are some backend issues why we cannot yet change
            this.tokenService.setToken(response['access_token']);
            this.authService.enableSsoAutoRelogin();
            this.ssoOauthStateService.removeState(params.state);
            this.authService.goToApp();
          },
          error: () => {
            this.isLoading.set(false);
            this.errorMessage.set('error.sso.couldNotLogin');
          }
        });
    });
  }

  private showErrorIfTokenIsInvalid(message: string): void {
    const hasValidToken = this.tokenService.hasValidToken();
    if (hasValidToken) {
      this.authService.goToApp();
    } else {
      this.isLoading.set(false);
      this.errorMessage.set(message);
    }
  }

  protected closeWindow(): void {
    if (window.opener) {
      window.close();
      return;
    }

    this.router.navigateByUrl(this.environment.loginRoute);
  }
}
